import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Download, RefreshCw, Search } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { normalizeDetail } from "../../lib/errors";
import {
  auditLogCsvUrl,
  listAuditLog,
  listAuditLogActions,
  listAuditLogActors,
} from "../../lib/adminApi";

/**
 * Admin audit-log browser (added 08-Feb-2026 as P1 · Task B). Reads from the `audit_logs`
 * collection which is already being written on every mutation across routes_admin,
 * routes_contact, routes_health_analyzer, etc. No new writes here — this is purely a read
 * surface + CSV export.
 *
 * Filters: actor (dropdown) · action (dropdown) · date range · free-text target search.
 * Server-paginated at 50 rows/page (server enforces ≤200). CSV export capped at 5000 rows.
 */

const ACTION_TONE = (action) => {
  if (!action) return "grey";
  if (action.endsWith("_created") || action.endsWith("_enrolled") || action.endsWith("_login")) return "green";
  if (action.endsWith("_updated") || action.endsWith("_refreshed")) return "amber";
  if (action.endsWith("_deleted")) return "red";
  return "grey";
};

const TONE_CLASSES = {
  green: "border-emerald-400/30 bg-emerald-400/10 text-emerald-300",
  amber: "border-amber-400/30 bg-amber-400/10 text-amber-300",
  red: "border-red-400/30 bg-red-400/10 text-red-300",
  grey: "border-border bg-elevated text-muted",
};

function ActionPill({ action }) {
  const tone = ACTION_TONE(action);
  return (
    <span
      className={`inline-flex whitespace-nowrap rounded-full border px-2 py-0.5 text-[10px] uppercase tracking-widest ${TONE_CLASSES[tone]}`}
      data-testid={`audit-action-${action}`}
    >
      {action}
    </span>
  );
}

function relativeTime(iso) {
  if (!iso) return "—";
  const then = new Date(iso);
  const diffMs = Date.now() - then.getTime();
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return `${sec}s ago`;
  if (sec < 3600) return `${Math.round(sec / 60)}m ago`;
  if (sec < 86400) return `${Math.round(sec / 3600)}h ago`;
  return `${Math.round(sec / 86400)}d ago`;
}

function absoluteTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleString("en-IN", {
    dateStyle: "medium",
    timeStyle: "medium",
  });
}

// Defaults: from = last 7 days, to = today. Reset button restores these.
function defaultFrom() {
  const d = new Date();
  d.setDate(d.getDate() - 7);
  return d.toISOString().slice(0, 10);
}
function defaultTo() {
  return new Date().toISOString().slice(0, 10);
}

export function AdminAuditLogPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [actors, setActors] = useState([]);
  const [actions, setActions] = useState([]);

  const [actorFilter, setActorFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [fromDate, setFromDate] = useState(defaultFrom());
  const [toDate, setToDate] = useState(defaultTo());
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const LIMIT = 50;

  const filters = useMemo(() => {
    const f = {};
    if (actorFilter) f.actor = actorFilter;
    if (actionFilter) f.action = actionFilter;
    if (fromDate) f.from = fromDate;
    if (toDate) f.to = toDate;
    if (search) f.search = search;
    return f;
  }, [actorFilter, actionFilter, fromDate, toDate, search]);

  const load = useCallback(async (targetPage = 1) => {
    setLoading(true);
    try {
      const data = await listAuditLog({ ...filters, page: targetPage, limit: LIMIT });
      setItems(data.items);
      setTotal(data.total);
      setPage(targetPage);
    } catch (err) {
      toast.error(normalizeDetail(err.response?.data?.detail, "Couldn't load the audit log."));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  // Load actor + action pickers once (they auto-refresh on manual refresh only — the lists don't
  // change often and re-fetching on every filter change would be wasteful).
  useEffect(() => {
    listAuditLogActors().then(setActors).catch(() => {});
    listAuditLogActions().then(setActions).catch(() => {});
  }, []);

  // Reload when any server-visible filter changes.
  useEffect(() => {
    load(1);
  }, [load]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setSearch(searchInput.trim());
  };

  const handleReset = () => {
    setActorFilter("");
    setActionFilter("");
    setFromDate(defaultFrom());
    setToDate(defaultTo());
    setSearch("");
    setSearchInput("");
  };

  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  return (
    <div className="mx-auto max-w-7xl px-4 py-8" data-testid="admin-audit-log-page">
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-widest text-gold">Compliance</p>
          <h1 className="mt-1 text-3xl font-semibold text-text">Audit log</h1>
          <p className="mt-1 text-sm text-muted">
            Every create / update / delete performed by an admin, and system-level events. Newest first.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => load(page)}
            disabled={loading}
            data-testid="audit-refresh-btn"
            className="gap-1.5"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <a
            href={auditLogCsvUrl(filters)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="audit-csv-download"
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-elevated px-3 py-2 text-sm text-text hover:border-gold hover:text-gold"
          >
            <Download className="h-3.5 w-3.5" /> Export CSV
          </a>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 grid gap-3 rounded-lg border border-border bg-card p-4 md:grid-cols-5">
        <div className="flex flex-col gap-1.5">
          <Label>Actor</Label>
          <select
            value={actorFilter}
            onChange={(e) => setActorFilter(e.target.value)}
            className="h-10 rounded-md border border-border bg-elevated px-3 text-sm text-text focus:border-gold focus:outline-none"
            data-testid="audit-filter-actor"
          >
            <option value="">All actors</option>
            {actors.map((a) => (
              <option key={a.id} value={a.id}>{a.label}</option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>Action</Label>
          <select
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            className="h-10 rounded-md border border-border bg-elevated px-3 text-sm text-text focus:border-gold focus:outline-none"
            data-testid="audit-filter-action"
          >
            <option value="">All actions</option>
            {actions.map((a) => (
              <option key={a} value={a}>{a}</option>
            ))}
          </select>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>From</Label>
          <Input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            data-testid="audit-filter-from"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label>To</Label>
          <Input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            data-testid="audit-filter-to"
          />
        </div>
        <form onSubmit={handleSearchSubmit} className="flex flex-col gap-1.5">
          <Label>Search target</Label>
          <div className="flex gap-1">
            <div className="relative flex-1">
              <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted" />
              <Input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="id, count=…"
                className="pl-7"
                data-testid="audit-filter-search"
              />
            </div>
            <Button type="submit" size="sm" data-testid="audit-search-btn">Go</Button>
          </div>
        </form>
        <div className="md:col-span-5">
          <Button variant="ghost" size="sm" onClick={handleReset} data-testid="audit-reset-btn">
            Reset filters
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-hidden rounded-lg border border-border bg-card">
        <table className="w-full text-sm">
          <thead className="bg-elevated/60 text-xs uppercase tracking-widest text-muted">
            <tr>
              <th className="px-4 py-3 text-left">When</th>
              <th className="px-4 py-3 text-left">Actor</th>
              <th className="px-4 py-3 text-left">Action</th>
              <th className="px-4 py-3 text-left">Target</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {loading && items.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-10 text-center text-sm text-muted">Loading…</td></tr>
            )}
            {!loading && items.length === 0 && (
              <tr><td colSpan={4} className="px-4 py-10 text-center text-sm text-muted" data-testid="audit-empty-row">
                No audit entries match these filters.
              </td></tr>
            )}
            {items.map((it) => (
              <tr key={it.id} className="hover:bg-elevated/40" data-testid={`audit-row-${it.id}`}>
                <td className="px-4 py-3 align-top">
                  <div className="text-text">{relativeTime(it.timestamp)}</div>
                  <div className="text-[10px] text-muted">{absoluteTime(it.timestamp)}</div>
                </td>
                <td className="px-4 py-3 align-top text-text">{it.actor_label}</td>
                <td className="px-4 py-3 align-top"><ActionPill action={it.action} /></td>
                <td className="px-4 py-3 align-top">
                  <code className="break-all text-xs text-muted">{it.target || "—"}</code>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="mt-4 flex items-center justify-between text-sm">
        <div className="text-muted">
          Showing {items.length} of {total} entries
          {totalPages > 1 && <> · page {page} of {totalPages}</>}
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={page <= 1 || loading}
            onClick={() => load(page - 1)}
            data-testid="audit-prev-page"
          >Prev</Button>
          <Button
            variant="outline"
            size="sm"
            disabled={page >= totalPages || loading}
            onClick={() => load(page + 1)}
            data-testid="audit-next-page"
          >Next</Button>
        </div>
      </div>
    </div>
  );
}

export default AdminAuditLogPage;
